import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { toast } from "sonner";
import { FileText, Download, Check, Ban, PlayCircle, Clock } from "lucide-react";

const STATUS_VARIANT = { paid: "success", issued: "warning", overdue: "destructive", void: "outline" };

function nowPeriod() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
}

export default function PlatformInvoices() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(nowPeriod());
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(() => {
    api.get("/billing/platform/invoices").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/billing/platform/config").then((r) => setLastRun(r.data.last_billing_run)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBilling = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/billing/platform/run-billing");
      toast.success(`Billing run complete — ${r.generated} invoice(s), ${r.overdue} overdue, ${r.reminders} reminder(s) sent`);
      setLastRun(r.last_billing_run);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const generateAll = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/billing/platform/invoices/generate-all", { period });
      toast.success(`Generated ${r.generated} invoice(s) for ${r.period}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const markPaid = async (id) => {
    try { await api.post(`/billing/platform/invoices/${id}/mark-paid`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const voidInv = async (id) => {
    try { await api.post(`/billing/platform/invoices/${id}/void`); toast.success("Invoice voided"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const download = async (inv) => {
    try {
      const r = await api.get(`/billing/invoices/${inv.id}.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = `${inv.invoice_number}.pdf`; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const totals = data?.totals || {};
  return (
    <div className="space-y-5" data-testid="platform-invoices">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex gap-3">
          <Money label="Issued (unpaid)" value={totals.issued} />
          <Money label="Paid" value={totals.paid} accent />
          <Money label="Overdue" value={totals.overdue} />
        </div>
        <div className="flex items-end gap-2">
          <div><div className="label-eyebrow text-[10px] mb-1">Billing period</div><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM" className="w-32" data-testid="gen-period" /></div>
          <Button variant="outline" onClick={generateAll} disabled={busy} data-testid="generate-all-btn"><PlayCircle className="h-4 w-4" /> Generate ({period})</Button>
          <Button variant="primary" onClick={runBilling} disabled={busy} data-testid="run-billing-btn"><Clock className="h-4 w-4" /> {busy ? "Running…" : "Run billing now"}</Button>
        </div>
      </div>
      {lastRun && <div className="text-[11px] text-muted-foreground -mt-2" data-testid="last-run">Last billing run: {new Date(lastRun).toLocaleString()} · auto-runs hourly (scheduler) — generates due invoices, marks overdue & sends dunning reminders.</div>}

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Invoice</TableHead><TableHead>Tenant</TableHead><TableHead>Period</TableHead>
            <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.invoices || []).map((inv) => (
              <TableRow key={inv.id} data-testid={`platform-invoice-${inv.id}`}>
                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">{inv.tenant_name}</TableCell>
                <TableCell className="text-xs">{inv.period}</TableCell>
                <TableCell className="text-right font-mono">S${formatNum(inv.total, { decimals: 2 })}</TableCell>
                <TableCell><Badge variant={STATUS_VARIANT[inv.status] || "outline"}>{inv.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => download(inv)} title="Download PDF"><Download className="h-3.5 w-3.5" /></Button>
                  {inv.status !== "paid" && inv.status !== "void" && <Button size="sm" variant="ghost" onClick={() => markPaid(inv.id)} title="Mark paid" data-testid={`markpaid-${inv.id}`}><Check className="h-3.5 w-3.5 text-[#00E65B]" /></Button>}
                  {inv.status !== "paid" && inv.status !== "void" && <Button size="sm" variant="ghost" onClick={() => voidInv(inv.id)} title="Void"><Ban className="h-3.5 w-3.5 text-red-500" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!(data?.invoices || []).length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8 text-sm">No invoices yet. Generate invoices for the current period above.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Money({ label, value, accent }) {
  return (
    <div className="card-flat px-4 py-2.5">
      <div className="label-eyebrow text-[9px]">{label}</div>
      <div className={`font-mono text-lg font-bold ${accent ? "text-accent" : ""}`}>S${formatNum(value || 0, { decimals: 2 })}</div>
    </div>
  );
}
